import { loadDemoDb, saveDemoDb } from "../demoDb";

const ok = (data, status = 200) => ({ data, status, statusText: "OK", headers: {} });
const notFound = (message = "Not found") => ok({ message }, 404);

function toInt(val) {
  const n = Number(val);
  return Number.isFinite(n) ? n : null;
}

function nextId(items = []) {
  return (items || []).reduce((m, x) => Math.max(m, toInt(x?.id) || 0), 0) + 1;
}

function pick(obj, keys = []) {
  const out = {};
  keys.forEach((k) => {
    if (obj && Object.prototype.hasOwnProperty.call(obj, k)) out[k] = obj[k];
  });
  return out;
}

function parseIdFromUrl(url) {
  const parts = String(url || "").split("/");
  return toInt(parts[parts.length - 1]);
}

function parseProfileSegmentFromUrl(url) {
  const parts = String(url || "").split("/").filter(Boolean);
  const idx = parts.indexOf("profile");
  if (idx === -1 || !parts[idx + 1]) return null;
  return parts[idx + 1];
}

const DEMO_INSTITUTION_COUNT = 10;

function buildDemoInstitutions(count = DEMO_INSTITUTION_COUNT) {
  return Array.from({ length: count }, (_, i) => {
    const n = i + 1;
    return {
      id: n,
      institution_name: `Тестовий заклад ${n}`,
      institution_route_id: String(n),
      address: "",
    };
  });
}

/** Поля для «Про нас» / вік; повертає збережені демо-дані з «Дубочок» на тестові назви (одноразово). */
function hydrateOsvitaAbout(db) {
  ensureOsvitaSeed(db);
  if (!db.osvita) return;
  let changed = false;

  if (!db.osvita.demoKgTestNamesRestored) {
    db.osvita.demoKgTestNamesRestored = true;
    const legacyNames = {
      Дубочок: "Тестовий заклад 1",
      "ЗДО с.Солонка": "Тестовий заклад 2",
    };
    const toTest = (name) => legacyNames[name] || name;
    for (const k of db.osvita.institutions || []) {
      const n = toTest(k.institution_name);
      if (n !== k.institution_name) {
        k.institution_name = n;
        changed = true;
      }
    }
    const touch = (row) => {
      if (!row?.institution_name) return;
      const n = toTest(row.institution_name);
      if (n !== row.institution_name) {
        row.institution_name = n;
        changed = true;
      }
    };
    for (const g of db.osvita.groups || []) touch(g);
    for (const r of db.osvita.dailyFoodCost || []) touch(r);
    for (const r of db.osvita.admins || []) touch(r);
    for (const r of db.osvita.paymentStatements || []) touch(r);
    for (const r of db.osvita.billing || []) touch(r);
  }

  for (const k of db.osvita.institutions || []) {
    if (k.address === undefined) {
      k.address = "";
      changed = true;
    }
  }

  for (const g of db.osvita.groups || []) {
    if (g.age_from === undefined || g.age_to === undefined) {
      if (g.group_type === "young") {
        g.age_from = 3;
        g.age_to = 4;
      } else if (g.group_type === "older") {
        g.age_from = 5;
        g.age_to = 6;
      } else {
        g.age_from = g.age_from ?? null;
        g.age_to = g.age_to ?? null;
      }
      changed = true;
    }
    const kg = (db.osvita.institutions || []).find((x) => String(x.institution_route_id) === String(g.institution_route_id));
    if (kg && g.institution_id == null) {
      g.institution_id = kg.id;
      changed = true;
    }
  }

  if (changed) saveDemoDb(db);
}

function resolveInstitutionRow(db, segment) {
  const list = db.osvita?.institutions || [];
  const id = toInt(segment);
  if (id != null) {
    const byId = list.find((k) => toInt(k.id) === id);
    if (byId) return byId;
  }
  return list.find((k) => String(k.institution_route_id) === String(segment)) || null;
}

function ensureDemoInstitutionCount(db, count = DEMO_INSTITUTION_COUNT) {
  ensureOsvitaSeed(db);
  const list = db.osvita.institutions || [];
  const byType = new Set(list.map((k) => String(k.institution_route_id)));
  let changed = false;
  for (const row of buildDemoInstitutions(count)) {
    if (!byType.has(row.institution_route_id)) {
      list.push(row);
      byType.add(row.institution_route_id);
      changed = true;
    }
  }
  if (changed) {
    db.osvita.institutions = list;
    saveDemoDb(db);
  }
}

function ensureOsvitaSeed(db) {
  if (!db.osvita) {
    const today = new Date().toISOString().slice(0, 10);
    db.osvita = {
      institutions: buildDemoInstitutions(),
      groups: [
        {
          id: 1,
          institution_id: 1,
          group_number: "1",
          institution_route_id: "1",
          group_name: "Тестова група 1",
          institution_name: "Тестовий заклад 1",
          group_type: "young",
          age_from: 3,
          age_to: 4,
        },
        {
          id: 2,
          institution_id: 1,
          group_number: "2",
          institution_route_id: "1",
          group_name: "Тестова група 2",
          institution_name: "Тестовий заклад 1",
          group_type: "older",
          age_from: 5,
          age_to: 6,
        },
      ],
      benefitReference: [
        { id: 1, name: "Тестова пільга 1 (50%)", size: 50, regulation: "ТЕСТ", institution_type: "kindergarten" },
        { id: 2, name: "Тестова пільга 2 (100%)", size: 100, regulation: "ТЕСТ", institution_type: "kindergarten" },
      ],
      benefits: [
        {
          id: 1,
          child_id: 1,
          benefit_reference_id: 1,
          benefit_name: "Тестова пільга 1",
          benefit_reason: "ТЕСТ",
          valid_from: today,
          valid_to: "",
          documents: "ТЕСТ",
          status: "active",
          institution_route_id: "1",
        },
      ],
      children: [
        { id: 1, child_name: "Тестова дитина 1", parent_name: "Тестовий батько 1", phone_number: "+380000000001", group_id: 1, institution_route_id: "1" },
        { id: 2, child_name: "Тестова дитина 2", parent_name: "Тестовий батько 2", phone_number: "+380000000002", group_id: 2, institution_route_id: "1" },
        { id: 3, child_name: "Тестова дитина 4", parent_name: "Тестовий батько 4", phone_number: "+380000000004", group_id: 1, institution_route_id: "2" },
      ],
      attendance: [
        {
          attendance_id: 1,
          child_id: 1,
          attendance_status: "present",
          date: today,
          institution_route_id: "1",
        },
        {
          attendance_id: 2,
          child_id: 2,
          attendance_status: "absent",
          date: today,
          institution_route_id: "1",
        },
        {
          attendance_id: 3,
          child_id: 3,
          attendance_status: "present",
          date: today,
          institution_route_id: "2",
        },
      ],
      pastAttendance: [
        {
          id: 1,
          child_id: 1,
          attendance_status: "present",
          date: today,
          institution_route_id: "1",
        },
        {
          id: 2,
          child_id: 2,
          attendance_status: "absent",
          date: today,
          institution_route_id: "1",
        },
      ],
      dailyFoodCost: [
        {
          id: 1,
          date: today,
          institution_name: "Тестовий заклад 1",
          young_group_cost: 50,
          older_group_cost: 60,
          institution_route_id: "1",
        },
      ],
      proposals: [
        {
          id: 1,
          child_name: "Тестова дитина 3",
          parent_name: "Тестовий батько 3",
          phone_number: "+380000000003",
          group_id: 1,
          benefit_reference_id: null,
          benefit_reason: "",
          status: "pending",
          review_notes: "",
          institution_route_id: "1",
          created_at: new Date().toISOString(),
        },
      ],
      admins: [
        {
          id: 1,
          username: "demo_educator_1",
          full_name: "Тестовий вихователь 1",
          institution_name: "Тестовий заклад 1",
          group_id: 1,
          role: "educator",
        },
      ],
      paymentStatements: [
        { id: 1, month: "2026-04", institution_name: "Тестовий заклад 1", group_name: "Тестова група 1", child_name: "Тестова дитина 1", amount: 0, created_at: new Date().toISOString() },
      ],
      billing: [
        {
          id: 1,
          child_name: "Тестова дитина 1",
          institution_name: "Тестовий заклад 1",
          group_name: "Тестова група 1",
          debt_amount: 980.46,
          charge_amount: 0,
          advance_amount: 0,
          paid_amount: 0,
          saldo: 980.46,
          payment_month: "2026-04",
        },
        {
          id: 2,
          child_name: "Тестова дитина 2",
          institution_name: "Тестовий заклад 1",
          group_name: "Тестова група 2",
          debt_amount: 726.46,
          charge_amount: 0,
          advance_amount: 0,
          paid_amount: 0,
          saldo: 726.46,
          payment_month: "2026-04",
        },
      ],
    };
  }
}

function listResponse(items = [], page = 1, limit = 16) {
  const totalItems = items.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / limit));
  const start = (page - 1) * limit;
  const paged = items.slice(start, start + limit);
  return { items: paged, totalItems, currentPage: page, totalPages };
}

export function canHandleOsvita({ url }) {
  return String(url || "").startsWith("api/institution/");
}

export function handleOsvita({ method, url, body }) {
  const m = String(method || "GET").toUpperCase();
  const u = String(url || "");
  const b = body || {};

  const db = loadDemoDb();
  ensureDemoInstitutionCount(db);
  hydrateOsvitaAbout(db);

  // ---- Institutions ----
  if (m === "GET" && u === "api/institution/institutions") {
    const list = (db.osvita.institutions || []).map((k) => ({
      id: k.id,
      name: k.institution_name,
      address: k.address || "",
      institution_route_id: k.institution_route_id,
      institution_type: k.institution_type || "kindergarten",
    }));
    return ok(list);
  }

  // ---- Groups ----
  if (m === "POST" && u === "api/institution/groups/filter") {
    const page = Number(b.page || 1);
    const limit = Number(b.limit || 16);
    const institution_route_id = b.institution_route_id ? String(b.institution_route_id) : null;
    const kn = b.institution_name ? String(b.institution_name).trim() : null;
    let groups = db.osvita.groups || [];
    if (institution_route_id) {
      groups = groups.filter((g) => String(g.institution_route_id) === institution_route_id);
    }
    if (kn) {
      groups = groups.filter((g) => String(g.institution_name || "") === kn);
    }
    return ok(listResponse(groups, page, limit));
  }
  if (m === "POST" && u === "api/institution/groups") {
    const kn = (b.institution_name && String(b.institution_name).trim()) || "Тестовий заклад 1";
    const kgRow = (db.osvita.institutions || []).find((k) => k.institution_name === kn);
    const institution_route_id = kgRow ? String(kgRow.institution_route_id) : String(b.institution_route_id || "1");
    const institution_id = kgRow ? kgRow.id : toInt(institution_route_id);
    const item = {
      id: nextId(db.osvita.groups),
      institution_id: institution_id || null,
      institution_name: kn,
      group_name: b.group_name || `Група ${nextId(db.osvita.groups)}`,
      group_type: b.group_type || "young",
      group_number: b.group_number || String(nextId(db.osvita.groups)),
      institution_route_id,
      age_from: b.age_from !== undefined && b.age_from !== "" ? Number(b.age_from) : null,
      age_to: b.age_to !== undefined && b.age_to !== "" ? Number(b.age_to) : null,
    };
    db.osvita.groups.unshift(item);
    saveDemoDb(db);
    return ok(item, 201);
  }
  if (m === "PUT" && u.startsWith("api/institution/groups/")) {
    const id = parseIdFromUrl(u);
    const idx = db.osvita.groups.findIndex((x) => toInt(x.id) === id);
    if (idx === -1) return notFound();
    const prev = db.osvita.groups[idx];
    const merged = {
      ...prev,
      ...pick(b, [
        "institution_name",
        "group_name",
        "group_type",
        "group_number",
        "institution_route_id",
        "age_from",
        "age_to",
      ]),
    };
    if (b.age_from === null) merged.age_from = null;
    if (b.age_to === null) merged.age_to = null;
    const kgRow = (db.osvita.institutions || []).find((k) => k.institution_name === merged.institution_name);
    if (kgRow) {
      merged.institution_route_id = String(kgRow.institution_route_id);
      merged.institution_id = kgRow.id;
    }
    db.osvita.groups[idx] = merged;
    saveDemoDb(db);
    return ok(db.osvita.groups[idx]);
  }
  if (m === "DELETE" && u.startsWith("api/institution/groups/")) {
    const id = parseIdFromUrl(u);
    db.osvita.groups = db.osvita.groups.filter((x) => toInt(x.id) !== id);
    saveDemoDb(db);
    return ok({ ok: true, demo: true });
  }
  if (m === "POST" && u === "api/institution/groups/by-institution") {
    const institutionName = b.institution_name ? String(b.institution_name) : null;
    const items = (db.osvita.groups || []).filter((g) => !institutionName || g.institution_name === institutionName);
    return ok(items);
  }

  // ---- Billing ----
  if (m === "POST" && u === "api/institution/billing/filter") {
    const page = Number(b.page || 1);
    const limit = Number(b.limit || 16);

    let items = db.osvita.billing || [];
    if (b.payment_month) {
      const month = String(b.payment_month);
      items = items.filter(x => !x.payment_month || String(x.payment_month) === month);
    }
    if (b.child_name) {
      const q = String(b.child_name).toLowerCase();
      items = items.filter(x => String(x.child_name || "").toLowerCase().includes(q));
    }
    if (b.institution_name) {
      const q = String(b.institution_name).toLowerCase();
      items = items.filter(x => String(x.institution_name || "").toLowerCase().includes(q));
    }
    if (b.group_id) {
      const gid = Number(b.group_id);
      const group = (db.osvita.groups || []).find(g => Number(g.id) === gid);
      if (group) items = items.filter(x => x.group_name === group.group_name);
    }

    return ok(listResponse(items, page, limit));
  }
  if (m === "POST" && u === "api/institution/billing") {
    const item = {
      id: nextId(db.osvita.billing),
      child_name: b.child_name || "Тестова дитина",
      institution_name: b.institution_name || "Тестовий заклад 1",
      group_name: b.group_name || "Тестова група 1",
      debt_amount: Number(b.debt_amount || 0),
      charge_amount: Number(b.charge_amount || 0),
      advance_amount: Number(b.advance_amount || 0),
      paid_amount: Number(b.paid_amount || 0),
      saldo: Number(b.saldo || (Number(b.debt_amount || 0))),
      payment_month: String(b.payment_month || "2026-04"),
    };
    db.osvita.billing.unshift(item);
    saveDemoDb(db);
    return ok(item, 201);
  }
  if (m === "PUT" && u.startsWith("api/institution/billing/")) {
    const id = parseIdFromUrl(u);
    const idx = db.osvita.billing.findIndex((x) => toInt(x.id) === id);
    if (idx === -1) return notFound();
    db.osvita.billing[idx] = { ...db.osvita.billing[idx], ...b, id: db.osvita.billing[idx].id };
    saveDemoDb(db);
    return ok(db.osvita.billing[idx]);
  }
  if (m === "DELETE" && u.startsWith("api/institution/billing/")) {
    const id = parseIdFromUrl(u);
    db.osvita.billing = db.osvita.billing.filter((x) => toInt(x.id) !== id);
    saveDemoDb(db);
    return ok({ ok: true, demo: true });
  }
  if (m === "POST" && u === "api/institution/billing/sync-all") {
    return ok({ ok: true, demo: true, message: "DEMO: sync-all disabled" });
  }
  if (m === "POST" && u === "api/institution/billing/parse-pdf") {
    return ok({ ok: true, demo: true, message: "DEMO: parse-pdf disabled" });
  }
  if (m === "POST" && u === "api/institution/billing/advance-preview") {
    return ok({ ok: true, demo: true, preview: [] });
  }
  if (m === "POST" && u === "api/institution/billing/calculate-advance") {
    return ok({ ok: true, demo: true, result: [] });
  }
  if (m === "POST" && u === "api/institution/billing/generate-zip") {
    return ok({ ok: true, demo: true, message: "DEMO: generate-zip disabled" });
  }
  if (m === "POST" && u === "api/institution/billing/receipt-packages/upload") {
    return ok({ ok: true, demo: true, message: "DEMO: receipt-packages upload disabled" });
  }
  if (m === "GET" && u.startsWith("api/institution/billing/receipt-packages/download")) {
    const emptyZip = new Uint8Array([0x50, 0x4b, 0x05, 0x06, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0]);
    const blob = new Blob([emptyZip], { type: "application/zip" });
    return {
      data: blob,
      status: 200,
      statusText: "OK",
      headers: { "content-disposition": "attachment; filename*=UTF-8''Demo_kvitantsii.zip" },
    };
  }
  if (m === "GET" && u.startsWith("api/institution/billing/receipt-packages/exists")) {
    const mG = u.match(/group_id=([^&]+)/);
    const gid = mG ? parseInt(decodeURIComponent(mG[1]), 10) : 0;
    return ok({ exists: Number.isFinite(gid) && gid === 1, demo: true });
  }
  if (m === "GET" && u.startsWith("api/institution/billing/generate/")) {
    return ok({ ok: true, demo: true, message: "DEMO: generate disabled" });
  }

  // ---- Benefit reference (CRUD) ----
  if (m === "GET" && (u === "api/institution/benefit-reference" || u.startsWith("api/institution/benefit-reference?"))) {
    const type = new URLSearchParams(u.split("?")[1] || "").get("institution_type");
    const list = db.osvita.benefitReference || [];
    if (!type) return ok(list);
    return ok(list.filter((x) => (x.institution_type || "kindergarten") === type));
  }
  if (m === "POST" && u === "api/institution/benefit-reference") {
    const item = {
      id: nextId(db.osvita.benefitReference),
      name: b.benefit_name || b.name || `Тестова пільга ${nextId(db.osvita.benefitReference)}`,
      size: Number(b.discount ?? b.size ?? 0),
      regulation: b.reason || b.regulation || "ТЕСТ",
      institution_type: b.institution_type === "school" ? "school" : "kindergarten",
    };
    db.osvita.benefitReference.unshift(item);
    saveDemoDb(db);
    return ok(item, 201);
  }
  if (m === "PUT" && u.startsWith("api/institution/benefit-reference/")) {
    const id = parseIdFromUrl(u);
    const idx = db.osvita.benefitReference.findIndex((x) => toInt(x.id) === id);
    if (idx === -1) return notFound();
    db.osvita.benefitReference[idx] = { ...db.osvita.benefitReference[idx], ...pick(b, ["name", "size", "regulation"]) };
    saveDemoDb(db);
    return ok(db.osvita.benefitReference[idx]);
  }
  if (m === "POST" && u === "api/institution/department-report") {
    const type = b.institution_type || null;
    const ids = Array.isArray(b.institution_ids)
      ? b.institution_ids.map(toInt).filter((id) => Number.isInteger(id) && id > 0)
      : [];
    const institutions = (db.osvita.institutions || []).filter((k) => {
      const instType = k.institution_type || "kindergarten";
      if (type && instType !== type) return false;
      if (ids.length && !ids.includes(toInt(k.id))) return false;
      return true;
    });
    const groups = db.osvita.groups || [];
    const children = db.osvita.children || [];
    const activeBenefits = (db.osvita.benefits || []).filter((item) => item.status === "active");
    const refs = db.osvita.benefitReference || [];
    const byInstitution = institutions.map((k) => {
      const groupIds = groups
        .filter((g) => toInt(g.institution_id) === toInt(k.id)
          || String(g.institution_route_id) === String(k.institution_route_id))
        .map((g) => toInt(g.id));
      const kids = children.filter((c) => groupIds.includes(toInt(c.group_id))
        || String(c.institution_route_id) === String(k.institution_route_id));
      const withBenefit = kids.filter((c) => activeBenefits.some((item) => toInt(item.child_id) === toInt(c.id))).length;
      return {
        institution_id: k.id,
        institution_name: k.institution_name || k.name,
        institution_type: k.institution_type || "kindergarten",
        children: kids.length,
        with_benefit: withBenefit,
        without_benefit: Math.max(0, kids.length - withBenefit),
        enrolled: 0,
      };
    });
    const byBenefit = refs
      .filter((row) => !type || (row.institution_type || "kindergarten") === type)
      .map((row) => ({
        benefit_reference_id: row.id,
        benefit_name: row.name,
        institution_type: row.institution_type || "kindergarten",
        discount: row.size,
        children_count: activeBenefits.filter((item) => toInt(item.benefit_reference_id) === toInt(row.id)).length,
      }));
    const childrenTotal = byInstitution.reduce((sum, row) => sum + row.children, 0);
    const withBenefitTotal = byInstitution.reduce((sum, row) => sum + row.with_benefit, 0);
    return ok({
      filters: {
        institution_type: type,
        institution_ids: ids,
        enrollment_year: b.enrollment_year || new Date().getFullYear(),
        enrollment_month: b.enrollment_month || null,
      },
      totals: {
        children: childrenTotal,
        children_with_benefit: withBenefitTotal,
        children_without_benefit: Math.max(0, childrenTotal - withBenefitTotal),
        enrolled: 0,
      },
      by_institution: byInstitution,
      by_benefit: byBenefit,
    });
  }
  if (m === "DELETE" && u.startsWith("api/institution/benefit-reference/")) {
    const id = parseIdFromUrl(u);
    db.osvita.benefitReference = db.osvita.benefitReference.filter((x) => toInt(x.id) !== id);
    saveDemoDb(db);
    return ok({ ok: true, demo: true });
  }

  // ---- Children roster ----
  if (m === "POST" && u === "api/institution/childrenRoster/filter") {
    const page = Number(b.page || 1);
    const limit = Number(b.limit || 16);
    const institution_route_id = b.institution_route_id ? String(b.institution_route_id) : null;
    const children = db.osvita.children
      .filter(c => !institution_route_id || String(c.institution_route_id) === institution_route_id)
      .map((c) => {
        const group = (db.osvita.groups || []).find((g) => toInt(g.id) === toInt(c.group_id));
        return {
          ...c,
          group_name: group?.group_name || "",
          institution_name: group?.institution_name || "",
        };
      });
    return ok(listResponse(children, page, limit));
  }
  if (m === "POST" && u === "api/institution/childrenRoster") {
    const item = {
      id: nextId(db.osvita.children),
      child_name: b.child_name || `Тестова дитина ${nextId(db.osvita.children)}`,
      parent_name: b.parent_name || `Тестовий батько ${nextId(db.osvita.children)}`,
      phone_number: b.phone_number || "+380000000000",
      group_id: toInt(b.group_id) || 1,
      institution_route_id: String(b.institution_route_id || "1"),
    };
    db.osvita.children.unshift(item);
    saveDemoDb(db);
    return ok(item, 201);
  }
  if (m === "PUT" && u.startsWith("api/institution/childrenRoster/")) {
    const id = parseIdFromUrl(u);
    const idx = db.osvita.children.findIndex((x) => toInt(x.id) === id);
    if (idx === -1) return notFound();
    db.osvita.children[idx] = { ...db.osvita.children[idx], ...pick(b, ["child_name", "parent_name", "phone_number", "group_id"]) };
    saveDemoDb(db);
    return ok(db.osvita.children[idx]);
  }
  if (m === "DELETE" && u.startsWith("api/institution/childrenRoster/")) {
    const id = parseIdFromUrl(u);
    db.osvita.children = db.osvita.children.filter((x) => toInt(x.id) !== id);
    saveDemoDb(db);
    return ok({ ok: true, demo: true });
  }

  // ---- Attendance ----
  if (m === "POST" && u === "api/institution/attendance/filter") {
    const page = Number(b.page || 1);
    const limit = Number(b.limit || 16);
    const institution_route_id = b.institution_route_id ? String(b.institution_route_id) : null;
    const date = b.date ? String(b.date) : null;

    // Return roster-like list: one row per child for the selected date
    const children = (db.osvita.children || []).filter((c) => !institution_route_id || String(c.institution_route_id) === institution_route_id);
    const rows = children.map((c) => {
      const group = (db.osvita.groups || []).find((g) => toInt(g.id) === toInt(c.group_id));
      const attendance = (db.osvita.attendance || []).find((a) =>
        toInt(a.child_id) === toInt(c.id) &&
        (!institution_route_id || String(a.institution_route_id) === institution_route_id) &&
        (!date || String(a.date) === date)
      );
      return {
        child_id: c.id,
        child_name: c.child_name,
        group_name: group?.group_name || "",
        institution_name: group?.institution_name || "",
        attendance_id: attendance?.attendance_id || null,
        attendance_status: attendance?.attendance_status || "absent",
        date: date || attendance?.date || null,
        institution_route_id: institution_route_id || c.institution_route_id,
      };
    });

    return ok(listResponse(rows, page, limit));
  }

  // Visual-only writes: record locally and succeed
  if (m === "POST" && u === "api/institution/attendance") {
    const item = { attendance_id: nextId(db.osvita.attendance), ...b, attendance_id_key: `DEMO_${Date.now()}` };
    db.osvita.attendance.unshift(item);
    saveDemoDb(db);
    return ok({ ok: true, demo: true, item }, 201);
  }
  if (m === "PUT" && u.startsWith("api/institution/attendance/")) {
    const id = parseIdFromUrl(u);
    const idx = (db.osvita.attendance || []).findIndex((x) => toInt(x.attendance_id) === id || toInt(x.id) === id);
    if (idx === -1) return notFound();
    db.osvita.attendance[idx] = { ...db.osvita.attendance[idx], ...b };
    saveDemoDb(db);
    return ok({ ok: true, demo: true });
  }
  if (m === "DELETE" && u.startsWith("api/institution/attendance/")) {
    const id = parseIdFromUrl(u);
    db.osvita.attendance = (db.osvita.attendance || []).filter((x) => (toInt(x.attendance_id) !== id && toInt(x.id) !== id));
    saveDemoDb(db);
    return ok({ ok: true, demo: true });
  }

  // ---- Past attendance ----
  if (m === "POST" && u === "api/institution/past_attendance/filter") {
    const page = Number(b.page || 1);
    const limit = Number(b.limit || 16);
    const institution_route_id = b.institution_route_id ? String(b.institution_route_id) : null;
    const month = b.month ? String(b.month) : null; // YYYY-MM
    const date = b.date ? String(b.date) : null; // YYYY-MM-DD

    const items = (db.osvita.pastAttendance || [])
      .filter((x) => !institution_route_id || String(x.institution_route_id) === institution_route_id)
      .filter((x) => {
        if (date) return String(x.date) === date;
        if (month) return String(x.date || "").startsWith(month);
        return true;
      })
      .map((x) => {
        const child = (db.osvita.children || []).find((c) => toInt(c.id) === toInt(x.child_id));
        const group = child ? (db.osvita.groups || []).find((g) => toInt(g.id) === toInt(child.group_id)) : null;
        return {
          ...x,
          child_name: child?.child_name || "",
          group_name: group?.group_name || "",
          institution_name: group?.institution_name || "",
        };
      });

    return ok(listResponse(items, page, limit));
  }
  if (m === "POST" && u === "api/institution/past_attendance/export-excel") {
    return ok({ ok: true, demo: true, message: "DEMO: export disabled" });
  }

  // ---- Daily food cost ----
  if (m === "POST" && u === "api/institution/daily_food_cost/filter") {
    const page = Number(b.page || 1);
    const limit = Number(b.limit || 16);
    return ok(listResponse(db.osvita.dailyFoodCost || [], page, limit));
  }
  if (m === "POST" && u === "api/institution/daily_food_cost/breakdown") {
    return ok({ ok: true, demo: true, items: [] });
  }
  if (m === "POST" && u === "api/institution/daily_food_cost/breakdown/child") {
    return ok({ ok: true, demo: true, items: [] });
  }
  if (m === "POST" && u === "api/institution/daily_food_cost") {
    const item = { id: nextId(db.osvita.dailyFoodCost), ...b };
    db.osvita.dailyFoodCost.unshift(item);
    saveDemoDb(db);
    return ok(item, 201);
  }
  if (m === "PUT" && u.startsWith("api/institution/daily_food_cost/")) {
    const id = parseIdFromUrl(u);
    const idx = (db.osvita.dailyFoodCost || []).findIndex((x) => toInt(x.id) === id);
    if (idx === -1) return notFound();
    db.osvita.dailyFoodCost[idx] = { ...db.osvita.dailyFoodCost[idx], ...b };
    saveDemoDb(db);
    return ok(db.osvita.dailyFoodCost[idx]);
  }
  if (m === "DELETE" && u.startsWith("api/institution/daily_food_cost/")) {
    const id = parseIdFromUrl(u);
    db.osvita.dailyFoodCost = (db.osvita.dailyFoodCost || []).filter((x) => toInt(x.id) !== id);
    saveDemoDb(db);
    return ok({ ok: true, demo: true });
  }

  // ---- Proposals ----
  if (m === "POST" && u === "api/institution/proposals/filter") {
    const page = Number(b.page || 1);
    const limit = Number(b.limit || 16);
    const status = b.status ? String(b.status) : null;
    const items = (db.osvita.proposals || [])
      .filter((p) => !status || String(p.status) === status)
      .map((p) => {
        const group = (db.osvita.groups || []).find((g) => toInt(g.id) === toInt(p.group_id));
        return {
          ...p,
          group_name: group?.group_name || "",
          institution_name: group?.institution_name || "",
        };
      });
    return ok(listResponse(items, page, limit));
  }
  if (m === "POST" && u === "api/institution/proposals") {
    const item = { id: nextId(db.osvita.proposals), status: "pending", created_at: new Date().toISOString(), review_notes: "", ...b };
    db.osvita.proposals.unshift(item);
    saveDemoDb(db);
    return ok(item, 201);
  }
  if (m === "POST" && u.includes("/approve")) {
    const id = toInt(u.split("/")[3]);
    const idx = (db.osvita.proposals || []).findIndex((x) => toInt(x.id) === id);
    if (idx === -1) return notFound();
    db.osvita.proposals[idx] = { ...db.osvita.proposals[idx], status: "approved" };
    saveDemoDb(db);
    return ok({ ok: true, demo: true });
  }
  if (m === "POST" && u.includes("/reject")) {
    const id = toInt(u.split("/")[3]);
    const idx = (db.osvita.proposals || []).findIndex((x) => toInt(x.id) === id);
    if (idx === -1) return notFound();
    db.osvita.proposals[idx] = { ...db.osvita.proposals[idx], status: "rejected", review_notes: String(b.review_notes || "") };
    saveDemoDb(db);
    return ok({ ok: true, demo: true });
  }

  // ---- Benefits ----
  if (m === "POST" && u === "api/institution/benefits/filter") {
    const page = Number(b.page || 1);
    const limit = Number(b.limit || 16);
    const items = (db.osvita.benefits || []).map((bn) => {
      const child = (db.osvita.children || []).find((c) => toInt(c.id) === toInt(bn.child_id));
      const group = child ? (db.osvita.groups || []).find((g) => toInt(g.id) === toInt(child.group_id)) : null;
      return {
        ...bn,
        child_name: child?.child_name || "",
        group_name: group?.group_name || "",
        institution_name: group?.institution_name || "",
      };
    });
    return ok(listResponse(items, page, limit));
  }
  if (m === "POST" && u === "api/institution/benefits") {
    const item = { id: nextId(db.osvita.benefits), ...b };
    db.osvita.benefits.unshift(item);
    saveDemoDb(db);
    return ok(item, 201);
  }
  if (m === "PUT" && u.startsWith("api/institution/benefits/")) {
    const id = parseIdFromUrl(u);
    const idx = (db.osvita.benefits || []).findIndex((x) => toInt(x.id) === id);
    if (idx === -1) return notFound();
    db.osvita.benefits[idx] = { ...db.osvita.benefits[idx], ...b };
    saveDemoDb(db);
    return ok(db.osvita.benefits[idx]);
  }
  if (m === "DELETE" && u.startsWith("api/institution/benefits/")) {
    const id = parseIdFromUrl(u);
    db.osvita.benefits = (db.osvita.benefits || []).filter((x) => toInt(x.id) !== id);
    saveDemoDb(db);
    return ok({ ok: true, demo: true });
  }

  // ---- Payment statements ----
  if (m === "POST" && u === "api/institution/payment_statements/monthly") {
    const page = Number(b.page || 1);
    const limit = Number(b.limit || 16);
    return ok(listResponse(db.osvita.paymentStatements || [], page, limit));
  }
  if (m === "PUT" && u.startsWith("api/institution/payment_statements/")) {
    const id = parseIdFromUrl(u);
    const idx = (db.osvita.paymentStatements || []).findIndex((x) => toInt(x.id) === id);
    if (idx === -1) return notFound();
    db.osvita.paymentStatements[idx] = { ...db.osvita.paymentStatements[idx], ...b };
    saveDemoDb(db);
    return ok(db.osvita.paymentStatements[idx]);
  }
  if (m === "DELETE" && u.startsWith("api/institution/payment_statements/")) {
    const id = parseIdFromUrl(u);
    db.osvita.paymentStatements = (db.osvita.paymentStatements || []).filter((x) => toInt(x.id) !== id);
    saveDemoDb(db);
    return ok({ ok: true, demo: true });
  }

  // ---- Admins ----
  if (m === "POST" && u === "api/institution/admins/filter") {
    const page = Number(b.page || 1);
    const limit = Number(b.limit || 16);
    return ok(listResponse(db.osvita.admins || [], page, limit));
  }
  if (m === "POST" && u === "api/institution/admins") {
    const item = { id: nextId(db.osvita.admins), ...b };
    db.osvita.admins.unshift(item);
    saveDemoDb(db);
    return ok(item, 201);
  }
  if (m === "PUT" && u.startsWith("api/institution/admins/")) {
    const id = parseIdFromUrl(u);
    const idx = (db.osvita.admins || []).findIndex((x) => toInt(x.id) === id);
    if (idx === -1) return notFound();
    db.osvita.admins[idx] = { ...db.osvita.admins[idx], ...b };
    saveDemoDb(db);
    return ok(db.osvita.admins[idx]);
  }
  if (m === "DELETE" && u.startsWith("api/institution/admins/")) {
    const id = parseIdFromUrl(u);
    db.osvita.admins = (db.osvita.admins || []).filter((x) => toInt(x.id) !== id);
    saveDemoDb(db);
    return ok({ ok: true, demo: true });
  }

  // ---- Generate word by debt id (legacy) ----
  if (m === "GET" && u.startsWith("api/institution/generate/")) {
    return ok({ ok: true, demo: true, message: "DEMO: generate disabled" });
  }

  return null;
}

